import { useQuery } from '@tanstack/react-query';
import { EstimatedTag } from './ui/EstimatedTag';
import type { KnowledgeGraph } from '@lizrd/contracts';
import { getResourceHistory } from '../lib/api';
import { Modal } from './ui/modal';
import { Progress } from './ui/progress';
import { TrendChart } from './ui/trend-chart';
import {
  acceleratorLabel,
  acceleratorOf,
  consoleUrl,
  PROVIDER_ACCENT,
  typeLabel,
  usd,
} from '../lib/graph-visuals';
import { NodeTypeIcon } from './node-icons';
import { Badge } from './ui/badge';

type GraphNode = KnowledgeGraph['nodes'][number];

type Metrics = {
  cpuPct?: number | null;
  memPct?: number | null;
  gpuPct?: number | null;
  gpuMemPct?: number | null;
  invocations?: number | null;
  dutyCyclePct?: number | null;
  throughput?: { value: number; unit: string } | null;
};

// Properties surfaced elsewhere in the modal, or too heavy/internal to list raw.
const HIDDEN_PROPS = new Set([
  'monthlyCost',
  'costSource',
  'metrics',
  'capabilities',
  'capabilityMap',
  'readmeExcerpt',
  'summary',
]);

const humanizeKey = (k: string) =>
  k
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

/** Simple key/value facts we can render (skip nested objects and heavy fields). */
function primitiveFacts(props: Record<string, unknown>): [string, string][] {
  return Object.entries(props)
    .filter(([k, v]) => !HIDDEN_PROPS.has(k) && (typeof v === 'string' || typeof v === 'number'))
    .map(([k, v]) => [humanizeKey(k), String(v)] as [string, string]);
}

function Gauge({ label, pct }: { label: string; pct: number }) {
  return (
    <div>
      <div className="flex justify-between text-[0.7rem] text-muted">
        <span>{label}</span>
        <span className="text-haze">{Math.round(pct)}%</span>
      </div>
      <Progress value={pct} variant={pct >= 80 ? 'warning' : 'iris'} size="sm" />
    </div>
  );
}

/**
 * A read-only detail view for one cloud resource — everything the graph knows
 * about it (cost, utilisation, owner, provider facts), shown in a clean modal.
 * Opened from the cost treemap in place of navigating away to the cloud console.
 */
export function ResourceDetailModal({
  node,
  envId,
  ownerName,
  onClose,
}: {
  node: GraphNode | null;
  envId: string;
  ownerName?: string;
  onClose: () => void;
}) {
  if (!node) return null;
  const accent = PROVIDER_ACCENT[node.provider] ?? '#8b93a7';
  const url = consoleUrl(node);
  const accel = acceleratorOf(node.properties as Record<string, unknown>);

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <NodeTypeIcon type={node.type} className="h-5 w-5 text-muted" />
          <div className="min-w-0">
            <h3 className="truncate font-display text-base font-bold text-bone" title={node.name}>
              {node.name}
            </h3>
            <p className="text-[0.7rem] text-muted">{typeLabel(node.type, node.provider)}</p>
          </div>
          <span
            className="ml-1 flex-none rounded-full px-2 py-0.5 text-[0.6rem] font-bold"
            style={{ backgroundColor: `${accent}22`, color: accent }}
          >
            {node.provider}
          </span>
          {accel && (
            <Badge
              variant="info"
              title={`${accel.gpuClass}-class · ${accel.vramGbPerCard || '—'} GB VRAM`}
            >
              {acceleratorLabel(accel)}
            </Badge>
          )}
        </div>
      }
      footer={
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="text-xs font-semibold text-viridian hover:underline"
          >
            Open in cloud console ↗
          </a>
        ) : (
          <span className="text-xs text-muted">No console link available</span>
        )
      }
    >
      <ResourceDetailContent node={node} envId={envId} ownerName={ownerName} />
    </Modal>
  );
}

/**
 * The body of the resource detail — cost, utilisation, cost-over-time, and
 * provider facts. Extracted from the modal so it can render standalone (from the
 * cost treemap) or embedded inside the Explorer architecture navigation modal.
 */
export function ResourceDetailContent({
  node,
  envId,
  ownerName,
}: {
  node: GraphNode;
  envId: string;
  ownerName?: string;
}) {
  const history = useQuery({
    queryKey: ['resource-history', envId, node.id],
    queryFn: () => getResourceHistory(envId, node.id),
  });
  const props = node.properties as Record<string, unknown>;
  const cost = typeof props.monthlyCost === 'number' ? props.monthlyCost : null;
  const usageBased = props.costSource === 'usage-based';
  const metrics = (props.metrics ?? {}) as Metrics;
  const accel = acceleratorOf(props);
  const facts = primitiveFacts(props);

  return (
    <div className="space-y-4">
      {/* Cost + owner headline */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Monthly Cost</div>
          {usageBased && cost == null ? (
            <div
              className="font-display text-lg font-bold text-haze"
              title="Billed by usage — see your bill by service in Estimated vs. Billed"
            >
              Usage-based
            </div>
          ) : (
            <div className="font-display text-2xl font-bold text-iris">
              {cost != null ? usd(cost) : '—'}
              {cost != null && <span className="text-sm text-muted">/mo</span>}
              {cost != null && <EstimatedTag className="ml-2" />}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Owned By</div>
          <div className="text-sm text-bone">{ownerName ?? 'Unattributed'}</div>
        </div>
      </div>

      {/* Utilisation */}
      {(metrics.cpuPct != null ||
        metrics.memPct != null ||
        metrics.gpuPct != null ||
        metrics.invocations != null ||
        metrics.throughput) && (
        <div className="space-y-2 border-t border-hairline pt-3">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Utilisation</div>
          {/* GPU is the headline signal for accelerated nodes — shown first. */}
          {metrics.gpuPct != null && <Gauge label="GPU (7-day avg)" pct={metrics.gpuPct} />}
          {metrics.gpuMemPct != null && (
            <Gauge label="GPU memory (7-day avg)" pct={metrics.gpuMemPct} />
          )}
          {accel && metrics.gpuPct == null && (
            <p className="text-[0.65rem] text-muted">
              GPU utilisation unavailable — install the CloudWatch agent / DCGM exporter for
              accelerator-aware rightsizing.
            </p>
          )}
          {metrics.cpuPct != null && <Gauge label="CPU (7-day avg)" pct={metrics.cpuPct} />}
          {metrics.memPct != null && <Gauge label="Memory (7-day avg)" pct={metrics.memPct} />}
          {node.type === 'compute.instance' && metrics.memPct == null && (
            <p className="text-[0.65rem] text-muted">
              Memory unavailable — install the CloudWatch agent for memory-aware rightsizing.
            </p>
          )}
          {metrics.invocations != null && (
            <div className="flex justify-between text-xs">
              <span className="text-muted">Invocations (7-day)</span>
              <span className={metrics.invocations === 0 ? 'text-warning' : 'text-bone'}>
                {metrics.invocations === 0 ? '0 — idle' : metrics.invocations.toLocaleString()}
              </span>
            </div>
          )}
          {metrics.dutyCyclePct != null && (
            <div className="flex justify-between text-xs">
              <span className="text-muted">Active hours</span>
              <span className={metrics.dutyCyclePct < 50 ? 'text-warning' : 'text-bone'}>
                {Math.round(metrics.dutyCyclePct)}% of the window
              </span>
            </div>
          )}
          {metrics.throughput && (
            <div className="flex justify-between text-xs">
              <span className="text-muted">Throughput</span>
              <span className="text-bone">
                {metrics.throughput.value} {metrics.throughput.unit}
              </span>
            </div>
          )}
        </div>
      )}

      {/* History over time (CT-S3) */}
      {(history.data?.costMonthly.length ?? 0) > 1 && (
        <div className="space-y-1 border-t border-hairline pt-3">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Cost Over Time</div>
          <TrendChart
            points={history.data!.costMonthly}
            ariaLabel="Cost over time"
            height={44}
            formatValue={(v) => usd(v)}
          />
        </div>
      )}

      {/* Provider facts */}
      {facts.length > 0 && (
        <div className="space-y-2 border-t border-hairline pt-3">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Details</div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
            {facts.map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <dt className="text-muted">{k}</dt>
                <dd className="truncate text-bone" title={v}>
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
